import React, { useEffect, useState } from 'react';
import {
  sendNotification,
  deleteNotification,
  getNotifications,
  updateNotificationStatus,
} from '../../services/meals.api';
import { format } from 'date-fns';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Pagination
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

const NotificationPanel = () => {
  const [notifications, setNotifications] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentNotification, setCurrentNotification] = useState(null);
  const [form, setForm] = useState({ title: '', body: '', mealId: '', mealType: '', time: '', image: '' });
  const [tab, setTab] = useState('now');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, totalPages: 1 });


  const fetchNotifications = async () => {
    const res = await getNotifications({ page, limit: 10 });
    setNotifications(res.data);
    setPagination(res.data.pagination || {});
    setTotalPages(res.data.pagination?.totalPages || 1);
  };
  

  useEffect(() => {
    fetchNotifications();
  }, [page]);

  const handleSubmit = async () => {
    if (!form.title || !form.body) return alert('Title and body are required');

    const { title, body, imageUrl, time } = form;
    const payload = { title, body, imageUrl };
    if (tab === 'later' && time) payload.time = time;

    try {
      if (isEditing && currentNotification?._id) {
        await updateNotificationStatus(currentNotification._id, payload);
      } else {
        await sendNotification(payload);
      }
      fetchNotifications(page);
      resetForm();
      setOpenDialog(false);
    } catch (err) {
      console.error(err);
    }
  };

  const resetForm = () => {
    setForm({ title: '', body: '', mealId: '', mealType: '', time: '', image: '' });
    setCurrentNotification(null);
    setIsEditing(false);
  };

  const handleEdit = (notification) => {
    const { title, body, imageUrl, scheduledFor } = notification;
    console.log('Raw time from notification:', scheduledFor);

    const formattedTime = scheduledFor
      ? format(new Date(scheduledFor), "yyyy-MM-dd'T'HH:mm")
      : '';
  
    console.log('Formatted time:', formattedTime);
    setForm({ title, body, imageUrl, time: formattedTime });
    setCurrentNotification(notification);
    setIsEditing(true);
    setOpenDialog(true);
    setTab(notification.scheduledFor ? 'later' : 'now');
  };

  const handleDelete = async (id) => {
    await deleteNotification(id);
    fetchNotifications(page);
  };

  const handlePageChange = (event, value) => {
    setPage(value); // ✅ this will trigger useEffect and refetch
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold">
          Notifications
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            resetForm();
            setIsEditing(false);
            setOpenDialog(true);
          }}
        >
          + Create Notification
        </Button>
      </Box>

      <Grid container spacing={2}>
        {notifications?.data?.map((note) => (
          <Grid item xs={12} md={6} lg={4} key={note._id}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" justifyContent="space-between">
                  {note.status === 'pending' ? (
                    <>
                      <Typography color="green" fontWeight="bold">Scheduled</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {note.scheduledFor && format(new Date(note.scheduledFor), 'PPpp')}
                      </Typography>
                    </>
                  ) : (
                    <Typography color="green" fontWeight="bold"> {note.status.charAt(0).toUpperCase() + note.status.slice(1)}</Typography>
                  )}

                  <Box>
                    {note.status !== 'sent' && (
                      <IconButton onClick={() => handleEdit(note)} color="primary" size="small">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton onClick={() => handleDelete(note._id)} color="error" size="small">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                <Typography variant="h6">{note.title}</Typography>
                <Typography variant="body2" mb={1}>{note.body}</Typography>
                <Typography variant="caption" color="textSecondary">
                  Created: {format(new Date(note.createdAt), 'PPpp')}
                </Typography>
                <br />
                <Typography variant="caption" color="textSecondary">
                  Sent at: {format(new Date(note.time || note.createdAt), 'PPpp')}
                </Typography>
                <br />
                {note.status !== 'sent' && (
                  <Typography variant="caption" color="textSecondary">
                    Scheduled for: {note.scheduledFor ? format(new Date(note.scheduledFor), 'PPpp') : ''}
                  </Typography>
                )}
                <br />
                {note.imageUrl && (
                  <Box mt={2}>
                    <img src={note.imageUrl} alt="notification" style={{ maxHeight: '160px', width: '100%', borderRadius: 8 }} />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {totalPages > 0 && (
        <Box display="flex" justifyContent="center" mt={4}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
       )}
      {/* Modal */}
      <Dialog open={openDialog} onClose={() => { setOpenDialog(false); resetForm(); }} fullWidth maxWidth="sm">
        <DialogTitle>{isEditing ? 'Edit' : 'Create'} Notification</DialogTitle>
        <DialogContent>
        <ToggleButtonGroup
            value={tab}
            exclusive
            onChange={(e, newTab) => newTab && setTab(newTab)}
            sx={{ mb: 2 }}
            fullWidth
          >
            <ToggleButton value="now" disabled={isEditing && tab === 'later'}>
              Send Immediately
            </ToggleButton>
            <ToggleButton value="later">Schedule For Later</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            label="Title"
            fullWidth
            margin="dense"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
          <TextField
            label="Body"
            fullWidth
            multiline
            rows={3}
            margin="dense"
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
          />
          <TextField
            label="Image URL (optional)"
            fullWidth
            margin="dense"
            value={form.imageUrl}
            onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
          />
          {tab === 'later' && (
            <TextField
              label="Schedule Time"
              type="datetime-local"
              fullWidth
              margin="dense"
              InputLabelProps={{ shrink: true }}
              value={form.time}
              onChange={(e) => setForm({ ...form, time: e.target.value })}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" color="success">
            {isEditing ? 'Update' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotificationPanel;